import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Briefcase, User, Building2, ArrowRight, CheckCircle, Shield,
  Users, FileText, Search, Lock, Star, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const EspaceEmploiPage = () => {
  const { user, register, login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // null, 'register', 'login'
  const [role, setRole] = useState(null); // 'candidat', 'employeur'
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', company: '' });
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in with a role
  if (user?.role === 'candidat') { navigate('/espace-emploi/candidat'); return null; }
  if (user?.role === 'employeur') { navigate('/espace-emploi/employeur'); return null; }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'register') {
        const u = await register({ ...form, role });
        toast.success('Compte créé !');
        navigate(u.role === 'employeur' ? '/espace-emploi/employeur' : '/espace-emploi/candidat');
      } else {
        const u = await login(form.email, form.password);
        if (u.role === 'candidat') navigate('/espace-emploi/candidat');
        else if (u.role === 'employeur') navigate('/espace-emploi/employeur');
        else { toast.error("Ce compte n'est pas lie à l'Espace Emploi"); return; }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="espace-emploi-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0F766E] to-[#0D9488] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#F59E0B] text-white text-xs font-semibold px-3 py-1 rounded-full mb-5">
              <Briefcase className="w-3 h-3" /> ESPACE EMPLOI
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-4" data-testid="espace-emploi-title">
              La plateforme emploi de la Polynésie française
            </h1>
            <p className="text-lg text-white/70 leading-relaxed max-w-xl">
              Candidats, trouvez votre prochain poste. Employeurs, publiez vos offres et accédez aux meilleurs talents polynésiens. AERH modère et garantit la qualité.
            </p>
          </div>
        </div>
      </section>

      {/* Role choice cards */}
      {!mode && (
        <div className="max-w-5xl mx-auto px-4 -mt-10 relative z-20 mb-16">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Candidat */}
            <div className="bg-white rounded-2xl shadow-lg border-2 border-transparent hover:border-[#0D9488] transition-all p-8 cursor-pointer group" data-testid="role-candidat-card"
              onClick={() => { setRole('candidat'); setMode('register'); }}>
              <div className="w-14 h-14 rounded-2xl bg-[#0D9488]/10 flex items-center justify-center mb-5 group-hover:bg-[#0D9488]/20 transition-colors">
                <User className="w-7 h-7 text-[#0D9488]" />
              </div>
              <h3 className="text-xl font-bold text-[#1F2937] mb-2">Je suis candidat</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Créez votre profil, déposez votre CV, postulez aux offres et choisissez d'être contacté directement ou via AERH.
              </p>
              <ul className="space-y-2 mb-6">
                {['Inscription 100% gratuite', 'Profil et CV en ligne', 'Candidature en 1 clic', 'Mise en relation directe ou via AERH'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-[#0D9488] flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-2 text-[#0D9488] font-semibold text-sm">
                Créer mon profil <ArrowRight className="w-4 h-4" />
              </div>
            </div>

            {/* Employeur */}
            <div className="bg-white rounded-2xl shadow-lg border-2 border-transparent hover:border-[#F59E0B] transition-all p-8 cursor-pointer group" data-testid="role-employeur-card"
              onClick={() => { setRole('employeur'); setMode('register'); }}>
              <div className="w-14 h-14 rounded-2xl bg-[#F59E0B]/10 flex items-center justify-center mb-5 group-hover:bg-[#F59E0B]/20 transition-colors">
                <Building2 className="w-7 h-7 text-[#F59E0B]" />
              </div>
              <h3 className="text-xl font-bold text-[#1F2937] mb-2">Je suis employeur</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Publiez vos offres d'emploi, consultez les profils candidats et gérez vos recrutements en toute simplicité.
              </p>
              <ul className="space-y-2 mb-6">
                {['2 offres gratuites pour démarrer', 'Publication par formulaire ou PDF', 'Dashboard de gestion', 'Abonnement illimité dès 500 F/mois'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-[#F59E0B] flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-2 text-[#F59E0B] font-semibold text-sm">
                Publier une offre <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* Already have an account */}
          <div className="text-center mt-8">
            <button onClick={() => setMode('login')} className="text-sm text-gray-500 hover:text-[#0D9488] underline" data-testid="login-link">
              Déjà inscrit ? Se connecter
            </button>
          </div>
        </div>
      )}

      {/* Auth form */}
      {mode && (
        <div className="max-w-md mx-auto px-4 -mt-10 relative z-20 mb-16">
          <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="auth-form">
            <div className="flex items-center gap-3 mb-6">
              {role === 'candidat' ? <User className="w-5 h-5 text-[#0D9488]" /> : role === 'employeur' ? <Building2 className="w-5 h-5 text-[#F59E0B]" /> : <Lock className="w-5 h-5 text-gray-400" />}
              <h2 className="text-lg font-bold text-[#1F2937]">
                {mode === 'login' ? 'Connexion Espace Emploi' : role === 'candidat' ? 'Inscription Candidat' : 'Inscription Employeur'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <div>
                  <Label className="text-xs font-semibold text-slate-500 uppercase">Nom complet</Label>
                  <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required className="mt-1" data-testid="register-name" />
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required className="mt-1" data-testid="register-email" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase">Mot de passe</Label>
                <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required className="mt-1" data-testid="register-password" />
              </div>
              {mode === 'register' && (
                <>
                  <div>
                    <Label className="text-xs font-semibold text-slate-500 uppercase">Telephone</Label>
                    <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="mt-1" data-testid="register-phone" />
                  </div>
                  {role === 'employeur' && (
                    <div>
                      <Label className="text-xs font-semibold text-slate-500 uppercase">Nom de l'entreprise</Label>
                      <Input value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} required className="mt-1" data-testid="register-company" />
                    </div>
                  )}
                </>
              )}
              <Button type="submit" disabled={loading} className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white font-semibold py-5" data-testid="submit-auth">
                {loading ? 'Chargement...' : mode === 'login' ? 'Se connecter' : "Créer mon compte"}
              </Button>
            </form>

            <div className="mt-4 text-center space-y-2">
              {mode === 'register' && (
                <button onClick={() => setMode('login')} className="text-sm text-gray-500 hover:text-[#0D9488] underline">
                Déjà un compte ? Se connecter
                </button>
              )}
              {mode === 'login' && (
                <button onClick={() => setMode(null)} className="text-sm text-gray-500 hover:text-[#0D9488] underline">
                  Pas encore de compte ? S'inscrire
                </button>
              )}
              <div>
                <button onClick={() => { setMode(null); setRole(null); }} className="text-xs text-gray-400 hover:text-gray-600">
                  Retour au choix du profil
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Features */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#1F2937] text-center mb-10">Pourquoi l'Espace Emploi AERH ?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Shield, color: '#0D9488', title: 'Modération AERH', desc: 'Chaque offre est vérifiée et validée par nos équipes avant publication.' },
              { icon: Users, color: '#F59E0B', title: 'Talents polynésiens', desc: 'Accès à un vivier de candidats locaux qualifiés et motivés.' },
              { icon: Star, color: '#0D9488', title: 'Simple et efficace', desc: 'Interface intuitive, candidature en 1 clic, gestion simplifiée.' },
            ].map((f, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: f.color + '15' }}>
                  <f.icon className="w-6 h-6" style={{ color: f.color }} />
                </div>
                <h3 className="font-bold text-[#1F2937] mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing for employers */}
      <section className="py-16 bg-[#F8FAFC]" data-testid="pricing-section">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#1F2937] text-center mb-3">Tarifs Employeur</h2>
          <p className="text-gray-500 text-center mb-10 text-sm">Commencez gratuitement, passez à l'illimité quand vous en avez besoin.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Free */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="font-bold text-[#1F2937] mb-1">Decouverte</h3>
              <p className="text-3xl font-bold text-[#0D9488]">Gratuit</p>
              <p className="text-xs text-gray-400 mb-5">2 offres incluses</p>
              <ul className="space-y-2 text-sm text-gray-600">
                {['2 offres d\'emploi', 'Publication par formulaire', 'Dashboard basique', 'Validation AERH'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#0D9488]" /> {f}</li>
                ))}
              </ul>
            </div>
            {/* Monthly */}
            <div className="bg-white rounded-xl p-6 border-2 border-[#F59E0B] relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F59E0B] text-white text-[10px] font-bold px-3 py-0.5 rounded-full uppercase">Populaire</div>
              <h3 className="font-bold text-[#1F2937] mb-1">Mensuel</h3>
              <p className="text-3xl font-bold text-[#F59E0B]">500 <span className="text-base font-normal text-gray-400">F/mois</span></p>
              <p className="text-xs text-gray-400 mb-5">Offres illimitées</p>
              <ul className="space-y-2 text-sm text-gray-600">
                {['Offres illimitées', 'Upload PDF/Word', 'Dashboard avancé', 'Accès aux profils candidats', 'Sans engagement'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#F59E0B]" /> {f}</li>
                ))}
              </ul>
            </div>
            {/* Annual */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="font-bold text-[#1F2937] mb-1">Annuel</h3>
              <p className="text-3xl font-bold text-[#0D9488]">5 000 <span className="text-base font-normal text-gray-400">F/an</span></p>
              <p className="text-xs text-gray-400 mb-5">2 mois offerts</p>
              <ul className="space-y-2 text-sm text-gray-600">
                {['Tout du plan Mensuel', '2 mois gratuits', 'Priorité sur les candidatures', 'Support dédié'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#0D9488]" /> {f}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-gradient-to-r from-[#0F766E] to-[#0D9488] text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-3">Prêt à démarrer ?</h2>
          <p className="text-white/70 mb-6 text-sm">Rejoignez la plateforme emploi de référence en Polynésie française.</p>
          <div className="flex justify-center gap-4">
            <Link to="/offres">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-semibold px-6 py-5">
                Voir les offres <Search className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EspaceEmploiPage;
